import { useState, useEffect } from 'react';
import { createDeliveryDetails, updateDeliveryDetails } from '../../api/delivery_details';
import logger from '../../utils/logger';

const LATE_REASONS = [
    'Traffic',
    'Vehicle Breakdown',
    'Driver Availability',
    'Warehouse Delay',
    'Weather',
    'Accident / Road Closure',
    'Previous Delivery Overrun',
    'Other',
];

const DELAY_REASONS = [
    'Traffic',
    'Warehouse Delay',
    'Booking Error',
    'Retailer Congestion',
    'Vehicle Breakdown',
    'Driver Availability',
    'Weather',
    'Accident / Road Closure',
    'Previous Delivery Overrun',
    'Customer Not Ready',
    'Other',
];

const emptyForm = {
    booked_time: '',
    arrival_time: '',
    departure_time: '',
    late: false,
    late_reason: '',
    delay_length: '',
    delay_reason: '',
    penalties: '',
};

const DeliveryDetailsModal = ({ show, onClose, route, existingRecord, isLoading, theme, canEdit }) => {
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const isLight = theme === 'light';
    const isNew = !existingRecord;

    const calcDelayLength = (departure, arrival) => {
        if (!departure || !arrival) return '';
        const toMins = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
        return toMins(departure) - toMins(arrival);
    };

    useEffect(() => {
        if (existingRecord) {
            const arrival = (existingRecord.arrival_time || '').slice(0, 5);
            const booked = (existingRecord.booked_time || '').slice(0, 5);
            const departure = (existingRecord.departure_time || '').slice(0, 5);
            setForm({
                booked_time: booked,
                arrival_time: arrival,
                departure_time: departure,
                late: existingRecord.late ?? false,
                late_reason: existingRecord.late_reason || '',
                delay_length: calcDelayLength(departure, arrival),
                delay_reason: existingRecord.delay_reason || '',
                penalties: existingRecord.penalties ?? '',
            });
        } else {
            setForm(emptyForm);
        }
        setError('');
    }, [existingRecord, show]);

    const handleChange = (field, value) => {
        setForm(prev => {
            const next = { ...prev, [field]: value };

            // Auto-fill late when arrival or booked time changes
            if (field === 'arrival_time' || field === 'booked_time') {
                const arrival = field === 'arrival_time' ? value : prev.arrival_time;
                const booked = field === 'booked_time' ? value : prev.booked_time;
                if (arrival && booked) {
                    next.late = arrival > booked;
                    if (!next.late) next.late_reason = '';
                }
            }

            // Auto-calculate delay_length (time on site) when arrival or departure changes
            if (field === 'arrival_time' || field === 'departure_time') {
                const departure = field === 'departure_time' ? value : prev.departure_time;
                const arrival = field === 'arrival_time' ? value : prev.arrival_time;
                next.delay_length = calcDelayLength(departure, arrival);
            }

            return next;
        });
    };

    const handleSave = async () => {
        setError('');

        // Validate: late reason required if late
        if (form.late && !form.late_reason) {
            setError('Please select a late reason.');
            return;
        }

        // Validate: penalties must be a valid non-negative number with at most 2 decimal places
        if (form.penalties !== '' && form.penalties !== null) {
            const penaltyVal = String(form.penalties).trim();
            if (!/^\d+(\.\d{1,2})?$/.test(penaltyVal)) {
                setError('Penalty must be a valid amount (e.g. 25 or 25.50) with no more than 2 decimal places.');
                return;
            }
            if (parseFloat(penaltyVal) < 0) {
                setError('Penalty amount cannot be negative.');
                return;
            }
        }

        // Validate: delay reason required if time on site exceeds 60 mins
        if (form.delay_length !== '' && form.delay_length > 60 && !form.delay_reason) {
            setError('Over 1 hour on site — please select a delay reason.');
            return;
        }

        setSaving(true);
        try {
            const toMins = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
            const computedDelayLength = (form.departure_time && form.arrival_time)
                ? toMins(form.departure_time) - toMins(form.arrival_time)
                : null;

            const payload = {
                ...form,
                booked_time: form.booked_time || null,
                arrival_time: form.arrival_time || null,
                departure_time: form.departure_time || null,
                delay_length: computedDelayLength,
                penalties: form.penalties !== '' ? parseFloat(form.penalties) : 0,
                late: form.late,
                late_reason: form.late ? (form.late_reason || null) : null,
                delay_reason: form.delay_reason || null,
            };

            let result;
            if (isNew) {
                result = await createDeliveryDetails({
                    ...payload,
                    route_id: route.id,
                    route_uuid: route.route_uuid,
                    route_ref: route.route_ref,
                    date: route.date,
                    customer_id: route.customer_id || null,
                });
            } else {
                result = await updateDeliveryDetails(existingRecord.id, payload);
            }

            if (result.status === 200 || result.status === 201) {
                onClose(result.data);
            } else {
                setError(result.data?.error || 'Failed to save delivery details.');
            }
        } catch (err) {
            logger.error('DeliveryDetailsModal save error:', err);
            setError('An unexpected error occurred.');
        } finally {
            setSaving(false);
        }
    };

    if (!show) return null;

    const inputStyle = {
        width: '100%',
        padding: '8px 10px',
        borderRadius: 6,
        border: isLight ? '1px solid #ccc' : '1px solid #555',
        background: isLight ? '#f9f9f9' : '#333',
        color: isLight ? '#222' : '#eee',
        fontSize: 14,
        boxSizing: 'border-box',
    };

    const labelStyle = {
        display: 'block',
        marginBottom: 4,
        fontSize: 12,
        fontWeight: 600,
        color: isLight ? '#555' : '#aaa',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
    };

    const fieldWrap = { marginBottom: 16 };

    const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 };
    const row3 = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: 'rgba(0,0,0,0.75)', zIndex: 9999,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
        }}>
            <div style={{
                background: isLight ? '#fff' : '#1e1e1e',
                color: isLight ? '#222' : '#eee',
                borderRadius: 12,
                border: isLight ? '1px solid #ddd' : '2px solid #444',
                padding: 28,
                width: 'calc(100% - 32px)',
                maxWidth: 900,
                boxSizing: 'border-box',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                position: 'relative',
            }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Delivery Details</h3>
                        <p style={{ margin: '4px 0 0', fontSize: 13, color: isLight ? '#888' : '#aaa' }}>
                            {route?.job || '—'} &bull; {route?.route_ref || 'No ref'} &bull; {route?.date || ''}
                        </p>
                    </div>
                    <button onClick={() => onClose(null)} style={{
                        background: 'transparent', border: 'none', fontSize: 22,
                        cursor: 'pointer', color: isLight ? '#444' : '#ccc', lineHeight: 1,
                    }}>&times;</button>
                </div>

                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: isLight ? '#666' : '#aaa' }}>
                        Loading...
                    </div>
                ) : (
                    <>
                        {/* Route endpoints */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 22 }}>
                            {[
                                {
                                    label: 'Collection Address',
                                    friendlyName: route?.collection_friendly_name,
                                    name: route?.collection_name,
                                    address: route?.collection_address,
                                    postcode: route?.collection_postcode,
                                    additionalInfo: route?.collection_additional_info,
                                },
                                {
                                    label: 'Delivery Address',
                                    friendlyName: route?.delivery_friendly_name,
                                    name: route?.delivery_name || route?.job,
                                    address: route?.delivery_address || route?.address,
                                    postcode: route?.delivery_postcode || route?.postcode,
                                    additionalInfo: route?.delivery_additional_info,
                                },
                            ].map(({ label, friendlyName, name, address, postcode, additionalInfo }) => (
                                <section key={label} style={{ border: isLight ? '1px solid #d8d8d8' : '1px solid #555', borderRadius: 6, padding: '14px 16px', background: isLight ? '#f8f9fa' : '#292929' }}>
                                    <div style={labelStyle}>{label}</div>
                                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{friendlyName || name || 'Not recorded'}</div>
                                    {friendlyName && name && name !== friendlyName && <div>{name}</div>}
                                    <div>{address || 'Address not recorded'}</div>
                                    <div>{postcode || 'Postcode not recorded'}</div>
                                    {additionalInfo && <div style={{ marginTop: 8, color: isLight ? '#666' : '#bbb', whiteSpace: 'pre-wrap' }}>{additionalInfo}</div>}
                                </section>
                            ))}
                        </div>

                        {/* Timing */}
                        <div style={row3}>
                            <div style={fieldWrap}>
                                <label style={labelStyle}>Booked Time</label>
                                <input type="time" style={inputStyle} value={form.booked_time}
                                    onChange={e => handleChange('booked_time', e.target.value)}
                                    disabled={!canEdit} />
                            </div>
                            <div style={fieldWrap}>
                                <label style={labelStyle}>Arrival Time</label>
                                <input type="time" style={inputStyle} value={form.arrival_time}
                                    onChange={e => handleChange('arrival_time', e.target.value)}
                                    disabled={!canEdit} />
                            </div>
                            <div style={fieldWrap}>
                                <label style={labelStyle}>Departure Time</label>
                                <input type="time" style={inputStyle} value={form.departure_time}
                                    onChange={e => handleChange('departure_time', e.target.value)}
                                    disabled={!canEdit} />
                            </div>
                        </div>

                        {/* Late reason — only shown when auto-calculated as late */}
                        {form.late && (
                            <div style={fieldWrap}>
                                <label style={labelStyle}>Late Reason</label>
                                <select style={inputStyle} value={form.late_reason}
                                    onChange={e => handleChange('late_reason', e.target.value)}
                                    disabled={!canEdit}>
                                    <option value="">— Select reason —</option>
                                    {LATE_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>
                        )}

                        {/* Delay */}
                        <div style={row2}>
                            <div style={fieldWrap}>
                                <label style={labelStyle}>Time on Site (mins)</label>
                                <div style={{
                                    ...inputStyle,
                                    color: form.delay_length === '' ? (isLight ? '#999' : '#666') :
                                        form.delay_length > 60 ? '#dc3545' : '#22c55e',
                                    fontWeight: form.delay_length !== '' ? 600 : 400,
                                    cursor: 'default',
                                    userSelect: 'none',
                                }}>
                                    {form.delay_length === '' ? 'Set arrival & departure times' :
                                        `${form.delay_length} mins${form.delay_length > 60 ? ' (delayed)' : ''}`}
                                </div>
                            </div>
                            <div style={fieldWrap}>
                                <label style={labelStyle}>Delay Reason</label>
                                <select style={inputStyle} value={form.delay_reason}
                                    onChange={e => handleChange('delay_reason', e.target.value)}
                                    disabled={!canEdit}>
                                    <option value="">— Select reason —</option>
                                    {DELAY_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Penalties */}
                        <div style={fieldWrap}>
                            <label style={labelStyle}>Penalties (£)</label>
                            <input type="number" step="0.01" min="0" style={inputStyle}
                                value={form.penalties} placeholder="0.00"
                                onChange={e => handleChange('penalties', e.target.value)}
                                disabled={!canEdit} />
                        </div>

                        {/* Status badges */}
                        {!isNew && (
                            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
                                <span style={{
                                    padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                                    background: form.late ? 'rgba(220,53,69,0.2)' : 'rgba(34,197,94,0.2)',
                                    color: form.late ? '#dc3545' : '#22c55e',
                                    border: `1px solid ${form.late ? '#dc3545' : '#22c55e'}`,
                                }}>
                                    {form.late ? 'Late' : 'On Time'}
                                </span>
                                {form.delay_length !== '' && form.delay_length !== null && (
                                    <span style={{
                                        padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                                        background: form.delay_length > 60 ? 'rgba(220,53,69,0.2)' : 'rgba(255,193,7,0.2)',
                                        color: form.delay_length > 60 ? '#dc3545' : '#ffc107',
                                        border: `1px solid ${form.delay_length > 60 ? '#dc3545' : '#ffc107'}`,
                                    }}>
                                        {`${form.delay_length} mins on site`}
                                    </span>
                                )}
                                {form.penalties > 0 && (
                                    <span style={{
                                        padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                                        background: 'rgba(220,53,69,0.2)', color: '#dc3545',
                                        border: '1px solid #dc3545',
                                    }}>
                                        £{parseFloat(form.penalties).toFixed(2)} penalty
                                    </span>
                                )}
                            </div>
                        )}

                        {error && (
                            <p style={{ color: '#dc3545', fontSize: 13, marginBottom: 12 }}>{error}</p>
                        )}

                        {/* Actions */}
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
                            <button onClick={() => onClose(null)} style={{
                                padding: '8px 20px', borderRadius: 6, border: isLight ? '1px solid #ccc' : '1px solid #555',
                                background: 'transparent', color: isLight ? '#555' : '#ccc',
                                cursor: 'pointer', fontSize: 14,
                            }}>
                                {canEdit ? 'Cancel' : 'Close'}
                            </button>
                            {canEdit && (
                                <button onClick={handleSave} disabled={saving} style={{
                                    padding: '8px 20px', borderRadius: 6, border: 'none',
                                    background: saving ? '#888' : '#1a9dd7',
                                    color: '#fff', cursor: saving ? 'not-allowed' : 'pointer',
                                    fontSize: 14, fontWeight: 600,
                                }}>
                                    {saving ? 'Saving…' : isNew ? 'Create Record' : 'Save Changes'}
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DeliveryDetailsModal;
